/**
 * Angebote, Auftragsbestätigungen und Rechnungen.
 *
 * Bis jetzt gab es das nur als Schnittstelle: Nummernkreise, PDF-Erzeugung
 * und die Anzeige im Kundenportal waren fertig, aber es fehlte die Stelle,
 * an der jemand eine Rechnung anlegt. Damit war die Abrechnung nicht
 * benutzbar — in der Produktion stand genau ein Beleg.
 *
 * Was ein Beleg zulässt, sagt der Server (`aenderbar`, `herausgebbar`,
 * `stornierbar`). Diese Seite baut die Regeln nicht nach; sonst liefen sie
 * auseinander, und es erschienen Knöpfe, die der Server ablehnt.
 *
 * Zwei Ansichten auf denselben Bestand, weil es zwei Fragen sind:
 *
 *   Nach Datum   Was ist zuletzt passiert? Die chronologische Liste aller
 *                Dokumente, mit Filtern nach Art und Zustand.
 *   Nach Kunde   Wie steht es mit diesem Kunden? Offene Summe, davon
 *                überfällig, wie viele Prüfungen noch auf eine Rechnung
 *                warten – und ein Knopf, der genau dafür eine schreibt.
 *
 * Bewusst eine Seite mit einem Umschalter und kein zweiter Menüpunkt: es
 * ist derselbe Bestand, nur anders sortiert, und das Menü ist schon lang.
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  BELEGARTTEXT,
  BELEGSTATUSTEXT,
  euro,
  useBelegAnlegen,
  useBelegUebersicht,
  useBelege,
  type Belegart,
  type Belegstatus,
  type KundenUebersicht,
} from '../api/belege'
import { useKunden } from '../api/abfragen'
import { Blatt } from '../bausteine/Blatt'
import { useEntprellt } from '../bausteine/Entprellt'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, datumDeutsch } from '../bausteine/Hinweise'

const ARTEN: Belegart[] = ['angebot', 'auftragsbestaetigung', 'rechnung', 'storno']

/* Die Farbe sagt, was zu tun ist, nicht was war: offene Rechnungen fallen
   auf, bezahlte treten zurück. */
const STATUSFARBE: Record<Belegstatus, string> = {
  entwurf: 'bg-slate-100 text-slate-700',
  herausgegeben: 'bg-marke-100 text-marke-700',
  angenommen: 'bg-stufe-ob-grund text-stufe-ob',
  abgelehnt: 'bg-slate-100 text-slate-600',
  bezahlt: 'bg-stufe-ob-grund text-stufe-ob',
  storniert: 'bg-slate-100 text-slate-600',
}

export function Belege() {
  const [ansicht, setzeAnsicht] = useState<'datum' | 'kunde'>('datum')
  const [neuOffen, setzeNeuOffen] = useState(false)

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">Belege</h1>
        <button
          type="button"
          className="knopf-haupt ml-auto"
          onClick={() => setzeNeuOffen(true)}
        >
          Neuer Beleg
        </button>
      </header>

      <div className="flex gap-2" role="tablist" aria-label="Ansicht">
        {(
          [
            ['datum', 'Nach Datum'],
            ['kunde', 'Nach Kunde'],
          ] as const
        ).map(([wert, text]) => (
          <button
            key={wert}
            type="button"
            role="tab"
            aria-selected={ansicht === wert}
            className={ansicht === wert ? 'knopf-haupt flex-1' : 'knopf-still flex-1'}
            onClick={() => setzeAnsicht(wert)}
          >
            {text}
          </button>
        ))}
      </div>

      {ansicht === 'datum' ? <NachDatum /> : <NachKunde />}

      {neuOffen && <BelegNeu schliessen={() => setzeNeuOffen(false)} />}
    </div>
  )
}

function NachDatum() {
  const [status, setzeStatus] = useState('')
  const [art, setzeArt] = useState('')
  const belege = useBelege({ status: status || undefined, art: art || undefined })

  if (belege.isLoading) return <Ladeanzeige />
  if (belege.error) return <Fehlerhinweis fehler={belege.error} />

  const liste = belege.data ?? []
  const offen = liste.filter((b) => b.ueberfaellig)

  return (
    <div className="space-y-4">

      {offen.length > 0 && (
        <div className="rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3">
          <span className="font-semibold text-stufe-2">
            {offen.length} {offen.length === 1 ? 'Rechnung ist' : 'Rechnungen sind'}{' '}
            überfällig
          </span>{' '}
          <span className="text-base">
            ({euro(offen.reduce((s, b) => s + Number(b.brutto), 0))})
          </span>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="b-art">Art</label>
          <select id="b-art" value={art} onChange={(e) => setzeArt(e.target.value)}>
            <option value="">Alle</option>
            {ARTEN.map((a) => (
              <option key={a} value={a}>
                {BELEGARTTEXT[a]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="b-status">Zustand</label>
          <select
            id="b-status"
            value={status}
            onChange={(e) => setzeStatus(e.target.value)}
          >
            <option value="">Alle</option>
            {Object.entries(BELEGSTATUSTEXT).map(([wert, text]) => (
              <option key={wert} value={wert}>
                {text}
              </option>
            ))}
          </select>
        </div>
      </div>

      {liste.length === 0 ? (
        <Leerhinweis
          titel="Keine Belege"
          text="Ein Angebot oder eine Rechnung beginnt mit „Neuer Beleg“."
        />
      ) : (
        <ul className="space-y-2">
          {liste.map((b) => (
            <li key={b.id}>
              <Link to={`/belege/${b.id}`} className="karte-knopf">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">
                    {b.nummer ?? 'Entwurf'} · {BELEGARTTEXT[b.art]}
                  </div>
                  <div className="truncate text-base text-slate-700">
                    {b.kunde_name}
                    {b.betreff ? ` · ${b.betreff}` : ''} · {datumDeutsch(b.datum)}
                  </div>
                  {b.ueberfaellig && (
                    <div className="text-base font-semibold text-stufe-2">
                      Fällig war {datumDeutsch(b.faellig_am)}
                    </div>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-semibold tabular-nums">{euro(b.brutto)}</div>
                  <span className={`marke ${STATUSFARBE[b.status]}`}>
                    {BELEGSTATUSTEXT[b.status]}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

    </div>
  )
}

/**
 * Die Rechnungsübersicht.
 *
 * Ein Kunde, eine Zeile, und der Knopf, der dort gebraucht wird. Sortiert
 * hat schon der Server: was überfällig ist, steht oben, dann was offen
 * ist, dann wer auf eine Rechnung wartet.
 *
 * „Rechnung schreiben" legt einen leeren Rechnungsentwurf für diesen
 * Kunden an und springt hinein. Wer stattdessen fertige Prüfungen
 * abrechnen will, geht über „Abrechnen" — dort entstehen die Zeilen von
 * selbst. Die Zeile mit den wartenden Prüfungen verweist deshalb dorthin.
 */
function NachKunde() {
  const uebersicht = useBelegUebersicht()
  const anlegen = useBelegAnlegen()
  const navigieren = useNavigate()
  const [suche, setzeSuche] = useState('')
  const [nurOffene, setzeNurOffene] = useState(false)
  // Entprellt, weil bei einigen hundert Kunden jede Taste sonst die ganze
  // Liste neu zeichnet.
  const gesucht = useEntprellt(suche.trim().toLowerCase())

  if (uebersicht.isLoading) return <Ladeanzeige />
  if (uebersicht.error) return <Fehlerhinweis fehler={uebersicht.error} />

  const alle = uebersicht.data ?? []
  const liste = alle.filter((k) => {
    if (nurOffene && k.offen_anzahl === 0 && k.offene_pruefungen === 0) return false
    if (!gesucht) return true
    return (
      k.kunde_name.toLowerCase().includes(gesucht) ||
      (k.kundennummer ?? '').toLowerCase().includes(gesucht)
    )
  })

  const offenGesamt = alle.reduce((s, k) => s + Number(k.offen_betrag), 0)
  const ueberfaelligGesamt = alle.reduce((s, k) => s + Number(k.ueberfaellig_betrag), 0)

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="karte">
          <div className="text-base text-slate-600">Offen</div>
          <div className="text-2xl font-bold tabular-nums">{euro(offenGesamt)}</div>
        </div>
        <div
          className={`karte ${ueberfaelligGesamt > 0 ? 'border-stufe-2 bg-stufe-2-grund' : ''}`}
        >
          <div className="text-base text-slate-600">Davon überfällig</div>
          <div
            className={`text-2xl font-bold tabular-nums ${
              ueberfaelligGesamt > 0 ? 'text-stufe-2' : ''
            }`}
          >
            {euro(ueberfaelligGesamt)}
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <label htmlFor="bu-suche">Kunde suchen</label>
          <input
            id="bu-suche"
            type="search"
            value={suche}
            onChange={(e) => setzeSuche(e.target.value)}
            placeholder="Name oder Kundennummer"
          />
        </div>
        <label className="flex items-center gap-3 sm:mb-3">
          <input
            type="checkbox"
            className="h-6 w-6"
            checked={nurOffene}
            onChange={(e) => setzeNurOffene(e.target.checked)}
          />
          <span className="font-normal">Nur mit Offenem</span>
        </label>
      </div>

      <Fehlerhinweis fehler={anlegen.error} />

      {liste.length === 0 ? (
        <Leerhinweis
          titel="Kein Kunde gefunden"
          text={
            gesucht
              ? 'Kein Kunde trägt diesen Namen oder diese Nummer.'
              : 'Es gibt noch keine aktiven Kunden.'
          }
        />
      ) : (
        <ul className="space-y-2">
          {liste.map((k) => (
            <Kundenzeile
              key={k.kunde_id}
              kunde={k}
              laeuft={anlegen.isPending}
              schreiben={() =>
                anlegen.mutate(
                  { kunde_id: k.kunde_id, art: 'rechnung' },
                  { onSuccess: (neu) => navigieren(`/belege/${neu.id}`) },
                )
              }
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function Kundenzeile({
  kunde,
  laeuft,
  schreiben,
}: {
  kunde: KundenUebersicht
  laeuft: boolean
  schreiben: () => void
}) {
  return (
    <li className="karte space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Link to={`/kunden/${kunde.kunde_id}`} className="font-semibold underline">
          {kunde.kunde_name}
        </Link>
        {kunde.kundennummer && (
          <span className="text-base text-slate-600 tabular-nums">
            {kunde.kundennummer}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-base">
        {kunde.offen_anzahl > 0 ? (
          <span>
            <span className="font-semibold tabular-nums">
              {euro(kunde.offen_betrag)}
            </span>{' '}
            <span className="text-slate-600">
              offen ({kunde.offen_anzahl}{' '}
              {kunde.offen_anzahl === 1 ? 'Rechnung' : 'Rechnungen'})
            </span>
          </span>
        ) : (
          <span className="text-slate-600">Nichts offen</span>
        )}

        {kunde.ueberfaellig_anzahl > 0 && (
          <span className="marke bg-stufe-2-grund text-stufe-2">
            {euro(kunde.ueberfaellig_betrag)} überfällig
          </span>
        )}
        {kunde.entwuerfe > 0 && (
          <span className="marke bg-slate-100 text-slate-700">
            {kunde.entwuerfe} {kunde.entwuerfe === 1 ? 'Entwurf' : 'Entwürfe'}
          </span>
        )}
        {kunde.letzte_rechnung_am && (
          <span className="text-slate-600">
            zuletzt {datumDeutsch(kunde.letzte_rechnung_am)}
          </span>
        )}
      </div>

      {kunde.offene_pruefungen > 0 && (
        <Link
          to="/abrechnen"
          className="block rounded-feld border-2 border-akzent-rand bg-akzent-grund p-2 text-base text-akzent-700"
        >
          {kunde.offene_pruefungen}{' '}
          {kunde.offene_pruefungen === 1 ? 'fertige Prüfung wartet' : 'fertige Prüfungen warten'}{' '}
          auf eine Rechnung — zum Sammeln
        </Link>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="knopf-zweit"
          disabled={laeuft}
          onClick={schreiben}
        >
          Rechnung schreiben
        </button>
        <Link
          to={`/kunden/${kunde.kunde_id}`}
          className="knopf-still"
        >
          Kundenakte
        </Link>
      </div>
    </li>
  )
}

function BelegNeu({ schliessen }: { schliessen: () => void }) {
  const kunden = useKunden()
  const anlegen = useBelegAnlegen()

  return (
    <Blatt
      titel="Neuer Beleg"
      schliessen={schliessen}
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="submit"
            form="beleg-neu"
            className="knopf-haupt flex-1"
            disabled={anlegen.isPending}
          >
            {anlegen.isPending ? 'Legt an …' : 'Anlegen'}
          </button>
        </div>
      }
    >
      <form
        id="beleg-neu"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          const f = new FormData(e.currentTarget)
          const text = (n: string) => String(f.get(n) ?? '').trim()
          anlegen.mutate(
            {
              kunde_id: text('kunde_id'),
              art: text('art'),
              datum: text('datum') || null,
              betreff: text('betreff') || null,
            },
            {
              onSuccess: (neu) => {
                schliessen()
                // Direkt hinein: ein Beleg ohne Positionen ist kein Beleg,
                // der nächste Handgriff ist ohnehin die erste Zeile.
                window.location.href = `/belege/${neu.id}`
              },
            },
          )
        }}
      >
        <div>
          <label htmlFor="bn-kunde">Kunde</label>
          <select id="bn-kunde" name="kunde_id" required defaultValue="">
            <option value="" disabled>
              Bitte wählen
            </option>
            {(kunden.data ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="bn-art">Art</label>
            <select id="bn-art" name="art" defaultValue="angebot">
              {ARTEN.filter((a) => a !== 'storno').map((a) => (
                <option key={a} value={a}>
                  {BELEGARTTEXT[a]}
                </option>
              ))}
            </select>
            <p className="mt-1 text-base text-slate-600">
              Eine Stornorechnung entsteht nicht hier, sondern aus einer
              vorhandenen Rechnung.
            </p>
          </div>
          <div>
            <label htmlFor="bn-datum">Datum</label>
            <input
              id="bn-datum"
              name="datum"
              type="date"
              defaultValue={new Date().toISOString().slice(0, 10)}
            />
          </div>
        </div>

        <div>
          <label htmlFor="bn-betreff">Betreff</label>
          <input
            id="bn-betreff"
            name="betreff"
            maxLength={200}
            placeholder="Hauptuntersuchung 2026"
          />
        </div>

        <Fehlerhinweis fehler={anlegen.error} />
      </form>
    </Blatt>
  )
}
